import React, { useEffect, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Box, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { MeasurementData } from '../../contexts/CatalogContext';
import { useCollections } from '../../hooks/useCollections';
import { ItemCollectionInfo } from '../../types/collection';
import { logger } from '../../utils/logger';
import {
  formatControlledVocabulary,
  formatSingleControlledVocabulary,
  formatCreationDate,
  formatHeroDimensionsDual,
  formatInscriptions,
  formatMedium,
} from '../../utils/catalogFormatters';
import { CollectionBadges } from '../CollectionBadges';
import { DescriptionField } from './DescriptionField';
import {
  parseArrayField,
  parseSignatures,
  joinSignatureField,
  formatEditions,
  flattenStepNotes,
  flattenStrOrArray,
  Edition,
  CreationDateData,
} from './utils';

interface DescriptionSectionProps {
  artworkId: string;
  title: string;
  catalogData: Record<string, unknown> | null;
  keywordsData: Record<string, unknown> | null;
  measurementData: MeasurementData | null;
  additionalInfoData: Record<string, unknown> | null;
  parsedCreationDate: unknown;
  /**
   * True only when the authenticated viewer is looking at their own catalog
   * (not a public route, not a delegated view). Controls whether album-pill
   * clicks navigate to /my-objects — prevents stale foreign-collection state
   * when a non-owner views a public item that belongs to another user's album
   * (GH#1160).
   */
  isOwnerView?: boolean;
}

const sectionHeadingSx = {
  fontSize: '0.85rem',
  letterSpacing: '0.12em',
  fontWeight: 700,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  mb: 1,
  mt: 2,
};

export const DescriptionSection: React.FC<DescriptionSectionProps> = ({
  artworkId,
  title,
  catalogData,
  keywordsData,
  measurementData,
  additionalInfoData,
  parsedCreationDate,
  isOwnerView = false,
}) => {
  const alternativeTitles = parseArrayField(catalogData?.alternativeTitles);
  const aliNumber = (keywordsData?.aliCatalogNumber as string | undefined) ?? '';
  const inventoryNumbers = (Array.isArray(keywordsData?.customCatalogNumbers)
    ? (keywordsData?.customCatalogNumbers as string[]).filter(Boolean).join(', ')
    : '');
  const creators = formatControlledVocabulary((catalogData?.creators as unknown[]) ?? []) ?? '';
  const createdDate = formatCreationDate(parsedCreationDate) ?? '';
  const mediaStr = formatMedium(
    catalogData?.techniques as unknown[] | undefined,
    catalogData?.materials as unknown[] | undefined,
    catalogData?.mediaDescription as string | undefined,
  ) ?? '';
  const mediaAdditionalComments = (catalogData?.mediaAdditionalComments as string | undefined) ?? '';

  const signatures = parseSignatures(catalogData?.signatures);
  const inscriptionsStr = formatInscriptions(signatures) ?? '';
  const signatureType = joinSignatureField(signatures, 'type');
  const signatureDisplay = joinSignatureField(signatures, 'display');
  const signatureLocation = joinSignatureField(signatures, 'location');

  const dimensionsStr = measurementData ? (formatHeroDimensionsDual(measurementData) ?? '') : '';
  const substrateDimsStr = (() => {
    if (!measurementData?.hasDifferentSubstrate) return '';
    const subDims = measurementData.substrateMeasurements;
    if (!subDims?.dimensions?.some((d) => d.value)) return '';
    return formatHeroDimensionsDual(subDims) ?? '';
  })();

  const workType = formatSingleControlledVocabulary(catalogData?.workType) ?? '';
  const techniques = formatControlledVocabulary((catalogData?.techniques as unknown[]) ?? []) ?? '';
  const materials = formatControlledVocabulary((catalogData?.materials as unknown[]) ?? []) ?? '';
  const substrate = formatControlledVocabulary((catalogData?.substrate as unknown[]) ?? []) ?? '';
  const hasGettyVocabulary = Boolean(workType || techniques || materials || substrate);

  const editionsStr = formatEditions(catalogData?.editions as Edition[] | undefined);
  const titleDisplay = (catalogData?.titleAsWritten as string | undefined) ?? '';
  const titleLocation = parseArrayField(catalogData?.titleLocation);

  const isDateObject = parsedCreationDate && typeof parsedCreationDate === 'object';
  const creationDateRecord = (isDateObject ? parsedCreationDate : {}) as CreationDateData;
  const creationDateDisplay = flattenStrOrArray(creationDateRecord.display);
  const creationDateLocation = flattenStrOrArray(creationDateRecord.location);

  const keywordsStr = (Array.isArray(keywordsData?.keywords)
    ? (keywordsData?.keywords as string[]).filter(Boolean).join(', ')
    : '');

  const { fetchItemCollections } = useCollections();
  const [albums, setAlbums] = useState<ItemCollectionInfo[]>([]);
  useEffect(() => {
    if (!artworkId) return;
    let cancelled = false;
    fetchItemCollections(artworkId)
      .then((list) => { if (!cancelled) setAlbums(list); })
      .catch((err) => logger.error('Failed to fetch item collections', err));
    return () => { cancelled = true; };
  }, [artworkId, fetchItemCollections]);

  const describeNotes = flattenStepNotes(catalogData);
  const measureNotes = flattenStepNotes(measurementData as unknown as Record<string, unknown>);
  const catalogKeywordsNotes = flattenStepNotes(keywordsData);
  const publicNotes = Array.isArray(additionalInfoData?.publicNotes)
    ? (additionalInfoData?.publicNotes as string[]).filter(Boolean)
    : [];

  const hasAdditionalNotes = Boolean(
    describeNotes.length ||
      measureNotes.length ||
      catalogKeywordsNotes.length ||
      publicNotes.length,
  );

  const renderNotesBlock = (label: string, lines: string[]) => {
    if (lines.length === 0) return null;
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {label}
        </Typography>
        {lines.map((note, idx) => (
          <Typography key={idx} variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {note}
          </Typography>
        ))}
      </Box>
    );
  };

  return (
    <Accordion data-testid="description-section">
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h3">Description</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 3,
            rowGap: 0.5,
          }}
        >
          <DescriptionField label="Title" value={title} />
          <DescriptionField label="Additional Titles" value={alternativeTitles} />
          <DescriptionField label="ALI Catalog Number" value={aliNumber} />
          <DescriptionField label="Inventory Number(s)" value={inventoryNumbers} />
          <DescriptionField label="Creator and Co-Creators" value={creators} />
          <DescriptionField label="Created Date" value={createdDate} />
          <DescriptionField label="Media" value={mediaStr} />
          <DescriptionField label="Additional Comments" value={mediaAdditionalComments} preserveWhitespace />
          <DescriptionField label="Inscriptions" value={inscriptionsStr} />
          <DescriptionField label="Dimensions" value={dimensionsStr} />
          <DescriptionField label="Substrate Dimensions" value={substrateDimsStr} />
        </Box>

        {hasGettyVocabulary && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={sectionHeadingSx}>Getty Vocabulary</Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 3,
                rowGap: 0.5,
              }}
            >
              <DescriptionField label="Work Type" value={workType} />
              <DescriptionField label="Technique" value={techniques} />
              <DescriptionField label="Material" value={materials} />
              <DescriptionField label="Substrate" value={substrate} />
            </Box>
          </Box>
        )}

        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 3,
            rowGap: 0.5,
          }}
        >
          <DescriptionField label="Edition Info" value={editionsStr} />
          <DescriptionField label="Title Display" value={titleDisplay} />
          <DescriptionField label="Title Location" value={titleLocation} />
          <DescriptionField label="Signature Type" value={signatureType} />
          <DescriptionField label="Signature Display" value={signatureDisplay} />
          <DescriptionField label="Signature Location" value={signatureLocation} />
          <DescriptionField label="Creation Date Display" value={creationDateDisplay} />
          <DescriptionField label="Creation Date Location" value={creationDateLocation} />
          <DescriptionField label="Keywords" value={keywordsStr} />
          {albums.length > 0 && (
            <DescriptionField label="Albums">
              <CollectionBadges itemId={artworkId} collections={albums} enableNavigation={isOwnerView} />
            </DescriptionField>
          )}
        </Box>

        {hasAdditionalNotes && (
          <Box sx={{ mt: 2 }} data-testid="description-additional-notes">
            <Typography sx={sectionHeadingSx}>Additional Notes</Typography>
            {renderNotesBlock('Describe Artwork', describeNotes)}
            {renderNotesBlock('Measurements', measureNotes)}
            {renderNotesBlock('Inventory & Albums', catalogKeywordsNotes)}
            {renderNotesBlock("Add'l Public", publicNotes)}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
